'use client'

import { useEffect, useRef } from 'react'
import { useAuthStore } from '@/stores/auth.store'
import { apiClient } from '@/lib/api/client'

/**
 * Requests notification permission, obtains an FCM token,
 * and registers it with the server. Runs once per session after login.
 */
export function useFCMToken() {
  const { isAuthenticated } = useAuthStore()
  const registeredRef = useRef(false)

  useEffect(() => {
    if (!isAuthenticated || registeredRef.current) return
    if (typeof window === 'undefined' || !('Notification' in window)) return

    registeredRef.current = true

    const register = async () => {
      try {
        // Dynamically import to keep firebase out of the initial bundle
        const { getFCMToken } = await import('@/lib/notification/firebase-client')
        const token = await getFCMToken()
        if (!token) return

        await apiClient.post('/notifications/token', { token })
        console.log('[FCM] Device token registered')

        // Send Firebase config to service worker
        const vapidKey = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY
        if (vapidKey && 'serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.ready
          registration.active?.postMessage({
            type: 'FIREBASE_CONFIG',
            config: {
              apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
              authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
              projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
              messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
              appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
            },
          })
        }
      } catch (err) {
        console.error('[FCM] Token registration failed:', err)
      }
    }

    register()
  }, [isAuthenticated])
}
